import { useSession } from "next-auth/react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { API_URL } from "../lib/constants";
import { getDiscussionsByPoolId } from "../api/discussions";
import { useRouter } from "next/router";
import { useState } from "react";
import toast from "react-hot-toast";
import Spinner from "./Icons/Spinner";
import ImageWithFallback from "./ImageWithFallback";
import { formatDistance } from "date-fns";
import PopConfirm from "./PopConfirm";
import Image from "next/image";

const DiscussionItem = (props) => {
  return (
    <div className="mt-3 mb-1 p-1 rounded-md shadow-md">
      <div className="flex flex-row">
        <div className="flex flex-col shrink-0">
          <div className="ml-0 drop-shadow-lg">
            <div className="">
              <ImageWithFallback
                src={`https://stgolfchampsprodeastus.blob.core.windows.net/user-profile/${props.user.id}/profile_small.jpg`}
                alt="Hello"
                width={40}
                height={40}
                fallBackSrc="https://stgolfchampsprodeastus.blob.core.windows.net/public/not_found.png"
                style="inline object-cover w-10 h-10 mr-2 rounded"
              />
            </div>
          </div>
          {/* {props.user.isAdmin && <Image layout='fill' src={'/favicon.png'}/>} */}
        </div>
        <div className="flex flex-col font-serif ml-2">
          <div className="flex flex-row grow items-center">
            <div className="text-sm font-bold">
              {props.user.first_name} {props.user.last_name}
              {props.user?.isAdmin && (
                <span className="ml-1">
                  <Image width={12} height={12} src={"/favicon.png"} />
                </span>
              )}
            </div>
            <div className="text-xs ml-2 text-slate-600">
              {formatDistance(Date.parse(props?.createdAt), new Date(), {
                addSuffix: true,
              })}
            </div>
            {props.canDelete && (
              <div className="ml-3 text-xs text-red-600">
                <PopConfirm
                  title="Are you sure?"
                  onConfirm={() => {
                    props.onDelete();
                  }}
                >
                  <button>delete</button>
                </PopConfirm>
              </div>
            )}
          </div>
          {props.user.isAdmin ? (
            <div dangerouslySetInnerHTML={{ __html: props.content }} />
          ) : (
            <div>{props.content}</div>
          )}
        </div>
      </div>
    </div>
  );
};

const Discussion = (props) => {
  const queryClient = useQueryClient();
  const [discussionText, setDiscussionText] = useState("");
  const { data: session }: any = useSession();
  const accessToken = session?.accessToken;
  const router = useRouter();
  const mutation = useMutation(
    "sendDiscussion",
    () =>
      fetch(`${API_URL}/discussions`, {
        method: "POST",
        headers: {
          Authorization: "Bearer " + accessToken,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ pool: props.poolId, content: discussionText }),
      })
        .then((res) => {
          if (res.ok) {
            return res.json();
          }
          throw res.statusText;
        })
        .catch((error) => {
          throw new Error(error);
        }),
    {
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries(["discussion", props.poolId]);
        setDiscussionText("");
      },
      onError: (error: Error) => {
        toast.error(
          `An error occurred whilst sending your chat - ${error.message}`,
        );
        setDiscussionText("");
      },
    },
  );

  const deletePost = useMutation(
    "deleteDiscussion",
    (pId) =>
      fetch(`${API_URL}/discussions/${pId}`, {
        method: "DELETE",
        headers: {
          Authorization: "Bearer " + accessToken,
          "Content-Type": "application/json",
        },
      })
        .then((res) => {
          if (res.ok) {
            return res.json();
          }
          throw res.statusText;
        })
        .catch((error) => {
          throw new Error(error);
        }),
    {
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries(["discussion", props.poolId]);
      },
      onError: (error: Error) => {
        toast.error(
          `An error occurred whilst delete your post - ${error.message}`,
        );
      },
    },
  );

  const { isLoading, error, data } = useQuery(
    ["discussion", props.poolId], //Add Pool ID here
    () => getDiscussionsByPoolId(accessToken, props.poolId),
    {
      enabled: !!accessToken && !!router.isReady,
    },
  );

  // if (isLoading) return <p>Loading...</p>;
  if (error) return <p>An error has occurred</p>;
  // if (data) console.log("Discussion data is ", data);

  const noDiscussions = () => (
    <div className="p-2 text-center font-serif text-slate-300">
      Nothing here... yet
      <br />
      🤐
    </div>
  );

  return (
    <div>
      <div className='h-[50px] bg-black text-white flex items-center px-[12px] font-["Montserrat"] justify-between rounded-[5px]'>
        <span className="font-bold text-[16px] flex-1 uppercase">Chat</span>
      </div>
      <div className="discussion-wrapper" ref={props.discussionRef}>
        <div className="flex flex-row p-2 bg-gray-200 shadow-md">
          <input
            className="grow font-serif p-1 focus:outline-none"
            value={discussionText}
            type="text"
            onChange={(e) => {
              setDiscussionText(e.target.value);
            }}
          />
          <button
            className="flex items-center justify-center bg-black text-white rounded h-10 px-[16px] text-center"
            onClick={(e) => {
              e.preventDefault();
              mutation.mutate();
            }}
          >
            {mutation.isLoading ? (
              <Spinner width="30" height="30" fillColor="#ffffff" />
            ) : (
              "Send"
            )}
          </button>
        </div>
        <div style={{}}>
          {isLoading ? (
            <div className="flex justify-center pt-10 ">Loading</div>
          ) : null}
          {data && data.length == 0 && noDiscussions()}
          {data &&
            data.map((item) => (
              <DiscussionItem
                key={item.id}
                {...item}
                canDelete={
                  item.user.id == session?.userId || session?.isAdmin
                    ? true
                    : false
                }
                onDelete={() => {
                  deletePost.mutate(item.id);
                }}
              />
            ))}
        </div>
      </div>
    </div>
  );
};

export default Discussion;
